import { useState, useEffect } from 'react';

interface HeroSectionProps {
  onCtaClick: () => void;
}

const rotatingPhrases = [
  'für Berufstätige mit wenig Zeit.',
  'für Menschen mit Rückenbeschwerden.',
  'für alle, die Ergebnisse wollen.',
  'für die, die persönliche Betreuung schätzen.',
];

export default function HeroSection({ onCtaClick }: HeroSectionProps) {
  const [visible, setVisible] = useState(false);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [phraseVisible, setPhraseVisible] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setPhraseVisible(false);
      setTimeout(() => {
        setPhraseIndex((prev) => (prev + 1) % rotatingPhrases.length);
        setPhraseVisible(true);
      }, 400);
    }, 3200);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="hero" className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/21ced8fd24dc0fd424527da81795d288.jpg')`,
        }}
      />
      {/* Dark overlay */}
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(160deg, rgba(5,14,10,0.92) 0%, rgba(8,20,14,0.78) 50%, rgba(4,12,8,0.93) 100%)' }}
      />

      <div
        className={`relative z-10 w-full max-w-5xl mx-auto px-6 pt-36 pb-28 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      >
        {/* Badge */}
        <div
          className="inline-flex items-center gap-2.5 border rounded-full px-5 py-2 mb-10 backdrop-blur-sm"
          style={{ backgroundColor: 'rgba(255,255,255,0.04)', borderColor: 'rgba(93,196,142,0.22)' }}
        >
          <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 animate-pulse" style={{ backgroundColor: '#5DC48E' }} />
          <span className="text-xs font-medium tracking-[0.18em] uppercase" style={{ fontFamily: 'Inter, sans-serif', color: 'rgba(93,196,142,0.85)' }}>
            EMS-Studio Pinneberg · Persönlich betreut
          </span>
        </div>

        {/* Headline */}
        <h1
          className="text-[38px] md:text-[60px] lg:text-[70px] font-bold text-white leading-[1.04] mb-5 tracking-tight"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          20 Minuten.<br />
          Einmal die Woche.<br />
          <em className="not-italic" style={{ color: '#5DC48E' }}>Sichtbare Ergebnisse.</em>
        </h1>

        {/* Rotating sub-headline */}
        <div className="mb-5 h-7 flex items-center">
          <span
            className="text-base md:text-lg font-medium transition-all duration-400"
            style={{
              fontFamily: 'Inter, sans-serif',
              color: 'rgba(255,255,255,0.55)',
              opacity: phraseVisible ? 1 : 0,
              transform: phraseVisible ? 'translateY(0)' : 'translateY(6px)',
              transition: 'opacity 0.4s ease, transform 0.4s ease',
            }}
          >
            EMS-Training {rotatingPhrases[phraseIndex]}
          </span>
        </div>

        {/* Subtext */}
        <p
          className="text-white/55 text-sm md:text-base max-w-xl leading-relaxed mb-3"
          style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
        >
          Körperformen Pinneberg ist das persönlich betreute EMS-Studio für Menschen,
          die fitter, straffer und beschwerdefreier werden wollen –
          ohne sich in einem anonymen Fitnessstudio zu verlieren.
        </p>
        <p
          className="text-white/35 text-sm max-w-lg leading-relaxed mb-10"
          style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
        >
          <strong className="text-white/55 font-medium">Dein Trainer ist bei dir – jede Minute.</strong>{' '}
          Kein Standardprogramm. Keine Massenabfertigung. Nur du, dein Körper und ein klares Ziel.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-14">
          <button
            onClick={() => { const el = document.getElementById('vier-wochen'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
            className="text-white font-semibold text-sm px-9 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap w-full sm:w-auto"
            style={{ backgroundColor: '#1A5C3D', fontFamily: 'Inter, sans-serif' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1E6B47')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#1A5C3D')}
          >
            4 Wochen unverbindlich testen →
          </button>
          <button
            onClick={onCtaClick}
            className="border border-white/20 hover:border-white/40 text-white/60 hover:text-white font-light text-sm px-8 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap w-full sm:w-auto"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Kostenloses Probetraining sichern
          </button>
        </div>

        {/* Social proof bar */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex items-center gap-2.5">
            <div className="flex gap-0.5">
              {[1,2,3,4,5].map((s) => (
                <div key={s} className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-star-fill text-yellow-400 text-xs"></i>
                </div>
              ))}
            </div>
            <span className="text-white/55 text-xs" style={{ fontFamily: 'Inter, sans-serif' }}>
              <strong className="text-white font-semibold">5,0</strong> · 20 Google-Bewertungen
            </span>
          </div>
          <div className="w-px h-3.5 bg-white/12 hidden sm:block" />
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-group-line text-white/30 text-xs"></i>
            </div>
            <span className="text-white/40 text-xs" style={{ fontFamily: 'Inter, sans-serif' }}>500+ zufriedene Kunden aus Pinneberg</span>
          </div>
          <div className="w-px h-3.5 bg-white/12 hidden sm:block" />
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-car-line text-white/30 text-xs"></i>
            </div>
            <span className="text-white/40 text-xs" style={{ fontFamily: 'Inter, sans-serif' }}>Parkplatz direkt vor dem Studio</span>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-arrow-down-s-line text-white/22 text-xl"></i>
        </div>
      </div>
    </section>
  );
}
